import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, HelpCircle, Paperclip, Send, X } from 'lucide-react';
import { answerProfileQuestion } from '../../api/profileQuestions';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import { uploadMessagingAttachment } from '../../api/uploads';
import { toast } from 'react-toastify';
import { useMessageHubStore } from '../../store/messageHubStore';
import { useUserStore } from '../../store/userStore';
import { MessageHubShell } from './MessageHubShell';
import { MESSAGE_HUB_PAGE_SIZE } from './MessageHubListPagination';
import { QuestionSectionBlocks } from './QuestionSectionBlocks';
import { AttachmentView, type HubAttachment } from './AttachmentView';
import { QuestionThreadComposerBar, QuestionThreadMessageList } from './QuestionThreadSection';
import { useQuestionThread } from './useQuestionThread';
import {
  MessageHubListColumn,
  MessageHubDetailPanel,
  MessageHubEmptyDetail,
  MessageHubListItem,
  HubAvatar,
  HubStatusBadge,
  HubSectionChip,
} from './MessageHubShared';

interface Question {
  id: string;
  recruiter: string;
  company: string;
  section: string;
  question: string;
  questionItems?: Array<{ section?: string; questionText?: string }>;
  status: 'pending' | 'answered' | 'resolved';
  timeIso: string;
  timeDisplay: string;
  reply?: string;
  replyAttachments?: HubAttachment[];
  conversationId?: string;
}

function buildOptimisticReplyAttachments(files: File[]): HubAttachment[] {
  const file = files[0];
  if (!file) return [];
  return [
    {
      fileUrl: URL.createObjectURL(file),
      fileName: file.name,
      type: file.type.startsWith('image/')
        ? 'image'
        : file.type.startsWith('audio/')
          ? 'audio'
          : 'file',
      mimeType: file.type,
    },
  ];
}

function questionStatusVariant(status: Question['status']): 'unread' | 'success' | 'neutral' {
  if (status === 'answered') return 'success';
  if (status === 'resolved') return 'neutral';
  return 'unread';
}

export default function QuestionsHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');
  const [reply, setReply] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [pendingAnswerId, setPendingAnswerId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [listPage, setListPage] = useState(1);
  const questions = useMessageHubStore((state) => state.questions) as Question[];
  const loading = useMessageHubStore((state) => state.questionsLoading);
  const fetchQuestions = useMessageHubStore((state) => state.fetchQuestions);
  const setQuestions = useMessageHubStore((state) => state.setQuestions);
  const user = useUserStore((state) => state.user);

  useEffect(() => {
    void fetchQuestions().catch((error) => console.error(error));
  }, [fetchQuestions]);

  useEffect(() => {
    const targetId = searchParams.get('question');
    if (!targetId || questions.length === 0) return;
    const match = questions.find((q) => String(q.id) === String(targetId));
    if (!match) return;
    setSelectedQuestion(match);
    setMobileView('detail');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('question');
        return next;
      },
      { replace: true }
    );
  }, [questions, searchParams, setSearchParams]);

  useEffect(() => {
    setListPage(1);
  }, [search]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return questions;
    return questions.filter(
      (item) =>
        item.recruiter.toLowerCase().includes(q) ||
        item.company.toLowerCase().includes(q) ||
        item.question.toLowerCase().includes(q) ||
        item.section.toLowerCase().includes(q)
    );
  }, [questions, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / MESSAGE_HUB_PAGE_SIZE));
  const safeListPage = Math.min(listPage, totalPages);
  const pagedFiltered = useMemo(
    () =>
      filtered.slice(
        (safeListPage - 1) * MESSAGE_HUB_PAGE_SIZE,
        safeListPage * MESSAGE_HUB_PAGE_SIZE
      ),
    [filtered, safeListPage]
  );

  const handleSelectQuestion = async (q: Question) => {
    setSelectedQuestion(q);
    setMobileView('detail');

    if (q.status === 'pending') {
      try {
        await axiosInstance.patch(API_ROUTES.MESSAGESHUB.MARK_AS_READ(q.id));
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (files.length) {
      setSelectedFiles((prev) => [...prev, ...files]);
    }
    event.target.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if ((!reply.trim() && selectedFiles.length === 0) || !selectedQuestion || isSending) return;

    const answerText = reply.trim();
    const filesToSend = [...selectedFiles];
    const questionId = selectedQuestion.id;
    const optimisticReplyAttachments = buildOptimisticReplyAttachments(filesToSend);

    const applyOptimisticAnswer = () => {
      const patch = {
        status: 'answered' as const,
        reply: answerText,
        replyAttachments: optimisticReplyAttachments,
      };
      const currentQuestions = useMessageHubStore.getState().questions;
      setQuestions(currentQuestions.map((q) => (q.id === questionId ? { ...q, ...patch } : q)));
      setSelectedQuestion((prev) => (prev ? { ...prev, ...patch } : prev));
    };

    const revertOptimisticAnswer = () => {
      const currentQuestions = useMessageHubStore.getState().questions;
      setQuestions(
        currentQuestions.map((q) =>
          q.id === questionId
            ? {
                ...q,
                status: 'pending',
                reply: undefined,
                replyAttachments: undefined,
              }
            : q
        )
      );
      setSelectedQuestion((prev) =>
        prev
          ? {
              ...prev,
              status: 'pending',
              reply: undefined,
              replyAttachments: undefined,
            }
          : prev
      );
      setReply(answerText);
      setSelectedFiles(filesToSend);
    };

    applyOptimisticAnswer();
    setPendingAnswerId(questionId);
    setReply('');
    setSelectedFiles([]);

    try {
      setIsSending(true);
      let attachmentUrl: string | undefined;
      let attachmentType: string | undefined;
      let attachmentFileName: string | undefined;

      if (filesToSend.length > 0) {
        const file = filesToSend[0];
        const purpose = file.type.startsWith('image/')
          ? 'chat_image'
          : file.type.startsWith('audio/')
            ? 'chat_audio'
            : 'chat_file';
        const uploaded = await uploadMessagingAttachment(file, purpose);
        attachmentUrl = uploaded.publicUrl;
        attachmentType = uploaded.attachmentType;
        attachmentFileName = file.name;
      }

      const res = await answerProfileQuestion(questionId, {
        answerText,
        ...(attachmentUrl
          ? {
              attachment_url: attachmentUrl,
              attachment_type: attachmentType,
              attachmentFileName,
            }
          : {}),
      });

      if (!res?.success) {
        throw new Error(res?.message || 'Failed to submit answer');
      }

      const replyAttachments: HubAttachment[] = attachmentUrl
        ? [
            {
              fileUrl: attachmentUrl,
              fileName: attachmentFileName || 'attachment',
              type: attachmentType,
              mimeType: attachmentType,
            },
          ]
        : optimisticReplyAttachments;

      if (res.data && typeof res.data === 'object') {
        const row = res.data as Record<string, unknown>;
        useMessageHubStore.getState().upsertQuestionFromRealtime(row);
        const conv = row.conversation as Record<string, unknown> | undefined;
        const conversationId = conv?.id ? String(conv.id) : undefined;
        setSelectedQuestion((prev) =>
          prev
            ? {
                ...prev,
                status: 'answered',
                reply: answerText,
                replyAttachments,
                ...(conversationId ? { conversationId } : {}),
              }
            : prev
        );
      } else {
        const currentQuestions = useMessageHubStore.getState().questions;
        setQuestions(
          currentQuestions.map((q) =>
            q.id === questionId
              ? { ...q, status: 'answered', reply: answerText, replyAttachments }
              : q
          )
        );
      }

      if (!res.data || typeof res.data !== 'object') {
        setSelectedQuestion((prev) =>
          prev
            ? {
                ...prev,
                status: 'answered',
                reply: answerText,
                replyAttachments,
              }
            : prev
        );
      }
    } catch (error: unknown) {
      revertOptimisticAnswer();
      const message =
        error instanceof Error
          ? error.message
          : (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to submit answer';
      toast.error(message);
    } finally {
      setPendingAnswerId(null);
      setIsSending(false);
    }
  };

  const selected = questions.find((q) => q.id === selectedQuestion?.id) ?? selectedQuestion;
  const answerPending = pendingAnswerId !== null && pendingAnswerId === selected?.id;
  const threadSource =
    selected && selected.status === 'answered' && selected.conversationId ? selected : null;
  const questionThread = useQuestionThread(threadSource, user?.id);
  const showAnsweredThread = Boolean(threadSource);
  const showStaticAnswer =
    !showAnsweredThread &&
    (selected?.reply ||
      answerPending ||
      (selected?.replyAttachments && selected.replyAttachments.length > 0));

  const listColumn = (
    <MessageHubListColumn
      title="Questions"
      searchPlaceholder="Search questions…"
      search={search}
      onSearchChange={setSearch}
      loading={loading}
      loadingLabel="Questions"
      emptyMessage="No questions match your search."
      listPage={safeListPage}
      totalItems={filtered.length}
      onPageChange={setListPage}
    >
      {pagedFiltered.map((q) => (
        <MessageHubListItem
          key={q.id}
          active={selectedQuestion?.id === q.id}
          onClick={() => void handleSelectQuestion(q)}
          avatar={<HubAvatar name={q.recruiter} />}
          name={q.recruiter}
          meta={q.company}
          preview={q.question}
          time={q.timeDisplay}
          badges={
            <>
              <HubSectionChip label={q.section} />
              <HubStatusBadge status={q.status} variant={questionStatusVariant(q.status)} />
            </>
          }
        />
      ))}
    </MessageHubListColumn>
  );

  const detailColumn = selected ? (
    <MessageHubDetailPanel
      onBack={() => setMobileView('list')}
      avatar={<HubAvatar name={selected.recruiter} large />}
      title={selected.recruiter}
      subtitle={selected.company}
      footer={
        selected.status === 'pending' ? (
          <>
            {selectedFiles.length > 0 ? (
              <div className="mh-attachments-strip mh-thread-composer-bar">
                {selectedFiles.map((file, index) => {
                  const isImage = file.type.startsWith('image/');
                  return (
                    <div key={`${file.name}-${index}`} className="mh-attachment-chip">
                      {isImage ? (
                        <img src={URL.createObjectURL(file)} alt={file.name} />
                      ) : (
                        <FileText size={28} className="my-2 text-slate-500" />
                      )}
                      <span className="truncate w-full text-center">{file.name}</span>
                      <button
                        type="button"
                        className="absolute top-0.5 right-0.5 rounded bg-white/90 p-0.5 text-slate-600"
                        aria-label="Remove attachment"
                        onClick={() => removeFile(index)}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : null}
            <div className="mh-composer mh-thread-composer-bar">
              <label htmlFor="mh-question-attachment" className="mh-composer-attach">
                <Paperclip className="h-5 w-5" aria-hidden />
                <input
                  title="Attach file"
                  id="mh-question-attachment"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
              <input
                type="text"
                className="mh-composer-input"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Write your reply…"
                disabled={isSending}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
              />
              <button
                type="button"
                className="mh-composer-send"
                onClick={() => void handleSend()}
                disabled={isSending || (!reply.trim() && selectedFiles.length === 0)}
              >
                <Send className="inline h-4 w-4" aria-hidden />
                {isSending ? '…' : 'Send'}
              </button>
            </div>
          </>
        ) : showAnsweredThread ? (
          <QuestionThreadComposerBar thread={questionThread} />
        ) : null
      }
    >
      <QuestionSectionBlocks
        source={{
          section: selected.section,
          questionText: selected.question,
          questionItems: selected.questionItems,
        }}
        badgeLabel="Question"
      />
      {showAnsweredThread ? <QuestionThreadMessageList thread={questionThread} /> : null}
      {showStaticAnswer ? (
        <div className="mh-bubble-row mh-bubble-row--mine">
          <div className="mh-bubble mh-bubble--mine">
            <span className="mh-bubble-tag">{answerPending ? 'Sending…' : 'Your answer'}</span>
            {selected.replyAttachments?.map((file, index) => (
              <div key={`${file.fileUrl}-${index}`} className="mb-2">
                <AttachmentView file={file} />
              </div>
            ))}
            {selected.reply ? <p className="mh-bubble-body">{selected.reply}</p> : null}
          </div>
        </div>
      ) : null}
    </MessageHubDetailPanel>
  ) : (
    <MessageHubEmptyDetail
      icon={<HelpCircle className="h-7 w-7" />}
      title="Select a question"
      description="Choose a question from the list to read it and send your reply."
    />
  );

  return (
    <MessageHubShell
      active="questions"
      mobileView={mobileView}
      layoutRevision={selected?.id ?? 'empty'}
      listColumn={listColumn}
      detailColumn={detailColumn}
    />
  );
}
